import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Clock,
  Inbox as InboxIcon,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { useMemo } from "react";

import { apiFetch } from "@/api/client";
import type { TicketListRow } from "@/api/schemas";
import { KpiCard } from "@/components/dashboard/kpi-card";
import { RecentActivityFeed } from "@/components/dashboard/recent-activity-feed";
import { StatusDistribution } from "@/components/dashboard/status-distribution";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/stores/auth";
import {
  computeKpis,
  formatDuration,
  newTicketsSparkline,
} from "@/lib/dashboard-metrics";

const DASHBOARD_LIMIT = 200;

function DashboardPage() {
  const userName = useAuth((s) => s.user?.name ?? "");

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["dashboard-tickets"],
    queryFn: () =>
      apiFetch<TicketListRow[]>(`/api/tickets?limit=${DASHBOARD_LIMIT}`),
    staleTime: 30_000,
  });

  const kpis = useMemo(
    () => computeKpis(tickets ?? []),
    [tickets],
  );
  const sparkline = useMemo(
    () => newTicketsSparkline(tickets ?? []),
    [tickets],
  );

  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }, []);

  if (isLoading && !tickets) {
    return <DashboardSkeleton />;
  }

  const list = tickets ?? [];
  const hasData = list.length > 0;

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={greeting(userName)} description={todayLabel} />
      <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6 sm:space-y-6 scrollbar-thin">
        {!hasData ? (
          <DashboardEmptyState />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 sm:gap-4">
              <KpiCard
                label="Open"
                value={String(kpis.openCount)}
                description="Tickets awaiting action"
                icon={InboxIcon as LucideIcon}
                sparkline={sparkline}
              />
              <KpiCard
                label="Avg first response"
                value={
                  kpis.avgFirstResponseMs !== null
                    ? formatDuration(kpis.avgFirstResponseMs)
                    : "—"
                }
                description={
                  kpis.avgFirstResponseSampleSize > 0
                    ? `${kpis.avgFirstResponseSampleSize} replied today`
                    : "No replies today yet"
                }
                icon={Clock as LucideIcon}
                tooltip="Average elapsed time from ticket creation to first reply, for tickets that received their first reply today."
              />
              <KpiCard
                label="SLA breach today"
                value={String(kpis.slaBreachToday)}
                description="Past first-response due"
                icon={AlertTriangle as LucideIcon}
                trend={{
                  delta: kpis.slaBreachToday,
                  direction: kpis.slaBreachToday === 0 ? "flat" : "up",
                  label: kpis.slaBreachToday === 0 ? "all clear" : "today",
                  polarity: "down-is-good",
                }}
              />
              <KpiCard
                label="New last 24h"
                value={String(kpis.newLast24h)}
                description="Tickets opened"
                icon={Plus as LucideIcon}
                sparkline={sparkline}
              />
            </div>

            <div className="grid gap-3 lg:grid-cols-2 sm:gap-4">
              <StatusDistribution tickets={list} />
              <RecentActivityFeed tickets={list} />
            </div>

            <div className="text-[11px] text-text-faint">
              Metrics derived from the most recent {Math.min(list.length, DASHBOARD_LIMIT)} tickets in
              this organisation. Server-side aggregations across longer windows are a future
              enhancement.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function greeting(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "Welcome back";
  const first = trimmed.split(/\s+/)[0];
  return `Welcome back, ${first}`;
}

function DashboardEmptyState() {
  return (
    <Card variant="default" padding="lg">
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-subtle">
          <InboxIcon className="h-6 w-6 text-text-muted" />
        </div>
        <div>
          <h2 className="text-base font-medium text-text">Nothing to show yet</h2>
          <p className="mt-1 max-w-sm text-sm text-text-muted">
            Connect a Gmail mailbox or wait for the first ticket to land. Metrics will appear
            here once tickets start arriving.
          </p>
        </div>
      </div>
    </Card>
  );
}

export const Route = createFileRoute("/_auth/dashboard/")({
  component: DashboardPage,
});
